import * as _ from 'lodash';
import { SeamlessApiClient, type SeamlessApiClientError } from '@mondaydotcomorg/api';

import {
  DEFAULT_MONDAY_API_VERSION,
  extractErrorFieldsFromJsonErrorResponse,
  extractErrorText,
  type MondayRequestFn,
  type MondayRequester,
} from './common';

// Kept out of common.ts on purpose: common.ts is bundled into the Workers too, and @mondaydotcomorg/api
// pulls in graphql-request, graphql and zod 3. Only import this file from the monday-embedded frontends.

// monday.api() from monday-sdk-js never timed out; the seamless client defaults to 60s and rejects while
// monday may still be running the request, which would report a mutation as failed when it went through
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

// The package is compiled to ES5, where `Error.call(this)` returns a fresh Error, so the thrown object never has
// SeamlessApiClientError's prototype and `instanceof` is always false. Check the `type` it sets instead.
function isSeamlessApiClientError(err: unknown): err is SeamlessApiClientError {
  return _.get(err, 'type') === 'SeamlessApiClientError';
}

export function createMondaySeamlessRequester(apiVersion = DEFAULT_MONDAY_API_VERSION): MondayRequester {
  const client = new SeamlessApiClient(apiVersion);

  const requester: MondayRequestFn = async (scope, query, variables, _abortSignal, options) => {
    try {
      // resolves with the raw postMessage reply: `data` is the GraphQL response body, so the result is `data.data`
      const response: any = await client.request(query, variables, options?.apiVersion ?? apiVersion, REQUEST_TIMEOUT_MS);

      return {
        type: 'success',
        data: response.data.data,
        graphqlQuery: query,
        graphqlVariables: variables,
        scope,
      };
    } catch (err) {
      if (isSeamlessApiClientError(err) && Array.isArray(err.response.errors)) {
        return {
          type: 'error',
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
          ...extractErrorFieldsFromJsonErrorResponse(err.response, 'runtime', variables),
        };
      } else {
        return {
          type: 'error',
          errorType: 'unknown',
          errorText: extractErrorText(err),
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
        };
      }
    }
  };

  return Object.assign(requester, { apiVersion });
}
